import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import AdminSidebar from './AdminSidebar';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ImpersonationBanner from './ImpersonationBanner';
import { getCurrentUser } from '../../services/api';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';

const AdminLayout = () => (
  <SidebarProvider>
    <AdminLayoutInner />
  </SidebarProvider>
);


const AdminLayoutInner = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openMobile } = useSidebar();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      const result = await getCurrentUser();
      if (result.success) {
        setUser(result.data);
        // F2: refresh cached admin_user so sidebar/cockpit have role +
        // modules_access from the server (defends against stale logins
        // saved before RBAC fields existed).
        try {
          const cached = JSON.parse(localStorage.getItem('admin_user') || '{}');
          localStorage.setItem('admin_user', JSON.stringify({ ...cached, ...result.data }));
        } catch {
          localStorage.setItem('admin_user', JSON.stringify(result.data));
        }
      } else {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
      }
      setLoading(false);
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00B4D8] mx-auto"></div>
          <p className="mt-4 text-[#6C757D]">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* F2 Impersonation Banner — visible only when in impersonation mode */}
        <ImpersonationBanner />

        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* Mobile hamburger */}
              <button onClick={openMobile}
                      data-testid="header-mobile-hamburger"
                      className="lg:hidden p-2 rounded-md hover:bg-slate-100 text-[#2B2D42]">
                <Menu className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-xl lg:text-2xl font-bold text-[#2B2D42] truncate">
                  {t('admin.title')}
                </h1>
                <p className="text-xs lg:text-sm text-[#6C757D] truncate">
                  {t('admin.subtitle')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 flex-shrink-0">
              <LanguageSwitcher showLabel={true} />
              {user && (
                <>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-[#2B2D42]">
                      {user.full_name}
                    </p>
                    <p className="text-xs text-[#6C757D]">{user.email}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-[#00B4D8] flex items-center justify-center text-white font-semibold flex-shrink-0">
                    {user.full_name?.charAt(0) || 'A'}
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
